import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, ShoppingCart, Trash2, Star } from 'lucide-react';
import { authAPI } from './services/api';
import { useCart } from './context/CartContext';
import Header from './components/Header'; 

const FavoritesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // 檢查登入狀態
  useEffect(() => {
    if (!authAPI.isAuthenticated()) {
      alert(t('errors.loginRequired') || '請先登入才能查看收藏');
      navigate('/auth');
      return;
    }
    
    loadFavorites();
  }, [navigate, t]);

  // 載入收藏列表
  const loadFavorites = async () => {
    try {
      setLoading(true);
      // 從 localStorage 獲取收藏列表
      const savedFavorites = localStorage.getItem('favorites');
      if (savedFavorites) {
        setFavorites(JSON.parse(savedFavorites));
      }
    } catch (error) {
      console.error('載入收藏失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  // 移除收藏
  const removeFavorite = (productId) => {
    const updatedFavorites = favorites.filter(item => item.id !== productId);
    setFavorites(updatedFavorites);
    localStorage.setItem('favorites', JSON.stringify(updatedFavorites));
  };

  // 加入購物車
  const handleAddToCart = async (product) => {
    try {
      const productData = {
        productId: product.id,
        productName: product.name,
        variant: '默認規格',
        price: product.price,
        originalPrice: product.originalPrice,
        quantity: 1,
        stock: product.stock || 100,
        imageUrl: product.image,
        sellerName: '官方旗艦店'
      };

      await addToCart(productData);
      alert(t('success.addedToCart') || '✅ 已加入購物車！');
    } catch (error) {
      alert(`❌ ${error.message}`);
    }
  };

  // 渲染商品圖片
  const renderProductImage = (product) => {
    if (product.imageBase64) {
      return (
        <img 
          src={product.imageBase64} 
          alt={product.name}
          className="w-full h-full object-cover"
        />
      );
    }
    return <span className="text-6xl">{product.image}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">⏳</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('loading') || '載入中...'}</h2>
          <p className="text-gray-600">正在載入您的收藏</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ✅ 頂部導航 - 使用 simple variant */}
      <Header variant="simple" title={t('common.favorites') || '我的收藏'} />

      {/* 麵包屑 */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <button onClick={() => navigate('/')} className="hover:text-orange-500">
              {t('common.home') || '首頁'}
            </button>
            <span>/</span>
            <span className="text-gray-900 font-medium">{t('common.favorites') || '我的收藏'}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* 標題 */}
        <div className="flex items-center gap-3 mb-6">
          <Heart className="w-8 h-8 text-orange-500 fill-orange-500" />
          <h1 className="text-3xl font-bold text-gray-800">我的收藏</h1>
          <span className="text-gray-500">({favorites.length} 件商品)</span>
        </div>

        {favorites.length === 0 ? (
          /* 空收藏狀態 */
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="text-8xl mb-4">💔</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">您還沒有收藏任何商品</h2>
            <p className="text-gray-600 mb-6">快去挑選喜歡的商品加入收藏吧！</p>
            <button
              onClick={() => navigate('/products')}
              className="inline-block bg-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-600 transition"
            >
              開始購物
            </button>
          </div>
        ) : (
          /* 收藏商品列表 */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                {/* 商品圖片 */}
                <div 
                  className="relative aspect-square bg-gray-100 flex items-center justify-center cursor-pointer"
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {renderProductImage(product)}
                  
                  {/* 移除收藏按鈕 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFavorite(product.id);
                    }}
                    className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:bg-red-50 transition-colors"
                  >
                    <Heart className="w-5 h-5 text-red-500 fill-red-500" />
                  </button>

                  {/* 折扣標籤 */}
                  {product.originalPrice && (
                    <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-md text-sm font-bold">
                      省 {Math.round((1 - product.price / product.originalPrice) * 100)}%
                    </div>
                  )}
                </div>

                {/* 商品資訊 */}
                <div className="p-4">
                  <h3 
                    className="font-semibold text-gray-800 mb-2 line-clamp-2 h-12 cursor-pointer hover:text-orange-500"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    {product.name}
                  </h3>

                  {/* 評分 */}
                  <div className="flex items-center gap-1 mb-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm font-medium">{product.rating}</span>
                    <span className="text-sm text-gray-500">({product.reviews})</span>
                  </div>

                  {/* 價格 */}
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-2xl font-bold text-orange-500">
                      TWD {product.price}
                    </span>
                    {product.originalPrice && (
                      <span className="text-sm text-gray-400 line-through">
                        TWD {product.originalPrice}
                      </span>
                    )}
                  </div>

                  {/* 操作按鈕 */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={cartLoading}
                      className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <ShoppingCart size={18} />
                      加入購物車
                    </button>
                    <button
                      onClick={() => removeFavorite(product.id)}
                      className="p-2 border border-gray-300 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                    >
                      <Trash2 size={18} className="text-gray-600 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 繼續購物提示 */}
        {favorites.length > 0 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => navigate('/products')}
              className="text-orange-500 hover:text-orange-600 font-semibold"
            >
              繼續尋找更多喜歡的商品 →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;